import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/utils-format";
import { Wallet, Coins, ArrowDown, TrendUp, CircleNotch, Paperclip, Trash } from "@phosphor-icons/react";
import { FileUploader, fileUrl } from "@/components/FileUploader";
import { toast } from "sonner";

export default function ReceiverPortal({ user }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);

  const loadFiles = () =>
    api
      .get("/storage/files", { params: { kind: "delivery_proof" } })
      .then((r) => setFiles(r.data))
      .catch(() => setFiles([]));

  useEffect(() => {
    api.get("/portal/my-account")
      .then((r) => setSummary(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load your account"));
    loadFiles();
  }, []);

  const removeFile = async (id) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await api.delete(`/storage/files/${id}`);
      toast.success("File removed");
      loadFiles();
    } catch {
      toast.error("Could not remove file");
    }
  };

  if (error) {
    return (
      <div className="p-8" data-testid="portal-page">
        <div className="max-w-lg border border-amber-300 bg-amber-50 p-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-amber-800 font-semibold mb-2">Account not linked</p>
          <h2 className="text-2xl font-bold tracking-tight mb-2">Waiting for account setup</h2>
          <p className="text-sm text-amber-900">{error}</p>
        </div>
        <DeliveryProofSection files={files} onUploaded={loadFiles} onRemove={removeFile} />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm text-zinc-500">
        <CircleNotch size={16} className="animate-spin" /> Loading your account…
      </div>
    );
  }

  const currency = summary.account.currency || "INR";
  const symbol = currency === "INR" ? "₹" : currency === "EUR" ? "€" : currency === "USD" ? "$" : currency + " ";
  const fmt = (n) => `${symbol}${Number(n || 0).toLocaleString()}`;

  return (
    <div className="p-8" data-testid="portal-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">My Portal</p>
        <h1 className="text-4xl font-black tracking-tighter" data-testid="portal-welcome">
          Welcome, {user.name || summary.account.owner}
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          {summary.account.account_name} · {summary.account.country} · {currency}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Metric label="Total Received" value={fmt(summary.total_received)} icon={Wallet} />
        <Metric label="Collected" value={fmt(summary.total_collected)} icon={ArrowDown} />
        <Metric label="Pending" value={fmt(summary.pending_collection)} icon={Coins} accent />
        <Metric label="Commission Earned" value={fmt(summary.commission_earned)} icon={TrendUp} />
      </div>

      <div className="border border-zinc-200 bg-white mb-6" data-testid="portal-payments-table">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <Wallet size={16} weight="bold" />
          <h3 className="text-sm font-bold uppercase tracking-widest">Payments Received ({summary.total_transactions})</h3>
        </div>
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-left px-4 py-2">Transaction</th>
              <th className="text-right px-4 py-2">Amount</th>
              <th className="text-right px-4 py-2">Commission</th>
            </tr>
          </thead>
          <tbody>
            {summary.payments.map((p) => (
              <tr key={p.payment_id} className="border-b border-zinc-100">
                <td className="px-4 py-2 text-xs">{formatDateTime(p.verified_at)}</td>
                <td className="px-4 py-2 text-xs font-mono">{p.transaction_id || "—"}</td>
                <td className="px-4 py-2 text-sm font-mono text-right">{fmt(p.amount)}</td>
                <td className="px-4 py-2 text-sm font-mono text-right text-emerald-700">{fmt(p.commission_earned)}</td>
              </tr>
            ))}
            {summary.payments.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-sm text-zinc-500">No payments yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border border-zinc-200 bg-white" data-testid="portal-collections-table">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <ArrowDown size={16} weight="bold" />
          <h3 className="text-sm font-bold uppercase tracking-widest">Collections ({summary.collections.length})</h3>
        </div>
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-left px-4 py-2">Method</th>
              <th className="text-left px-4 py-2">Reference</th>
              <th className="text-right px-4 py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {summary.collections.map((c) => (
              <tr key={c.collection_id} className="border-b border-zinc-100">
                <td className="px-4 py-2 text-xs">{formatDateTime(c.date)}</td>
                <td className="px-4 py-2 text-xs">{c.method}</td>
                <td className="px-4 py-2 text-xs font-mono">{c.reference || "—"}</td>
                <td className="px-4 py-2 text-sm font-mono text-right">{fmt(c.amount)}</td>
              </tr>
            ))}
            {summary.collections.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-sm text-zinc-500">No collections recorded.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border border-zinc-200 bg-white mt-6" data-testid="portal-delivery-proof">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <Paperclip size={16} weight="bold" />
          <h3 className="text-sm font-bold uppercase tracking-widest">Delivery Proof / Receipts</h3>
        </div>
        <div className="p-4 space-y-4">
          <FileUploader
            kind="delivery_proof"
            accept="image/*,application/pdf"
            label="Upload a delivery receipt or payment proof (image or PDF · max 10 MB)"
            testId="delivery-proof-uploader"
            onUploaded={loadFiles}
          />
          {files.length === 0 ? (
            <p className="text-sm text-zinc-500">No files uploaded yet.</p>
          ) : (
            <FilesGrid files={files} onRemove={removeFile} />
          )}
        </div>
      </div>
    </div>
  );
}

function DeliveryProofSection({ files, onUploaded, onRemove }) {
  return (
    <div className="border border-zinc-200 bg-white mt-6 max-w-3xl" data-testid="portal-delivery-proof">
      <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
        <Paperclip size={16} weight="bold" />
        <h3 className="text-sm font-bold uppercase tracking-widest">Delivery Proof / Receipts</h3>
      </div>
      <div className="p-4 space-y-4">
        <FileUploader
          kind="delivery_proof"
          accept="image/*,application/pdf"
          label="Upload a delivery receipt or payment proof (image or PDF · max 10 MB)"
          testId="delivery-proof-uploader"
          onUploaded={onUploaded}
        />
        {files.length === 0 ? (
          <p className="text-sm text-zinc-500">No files uploaded yet.</p>
        ) : (
          <FilesGrid files={files} onRemove={onRemove} />
        )}
      </div>
    </div>
  );
}

function FilesGrid({ files, onRemove }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="delivery-proof-list">
      {files.map((f) => {
        const isImage = (f.content_type || "").startsWith("image/");
        const src = fileUrl(f.download_url);
        const dl = fileUrl(f.download_url, { inline: false });
        return (
          <div key={f.file_id} className="border border-zinc-200 p-3" data-testid={`file-${f.file_id}`}>
            {isImage ? (
              <a href={dl} target="_blank" rel="noreferrer">
                <img src={src} alt={f.original_filename} className="w-full h-32 object-cover border border-zinc-100" />
              </a>
            ) : (
              <a href={dl} className="w-full h-32 flex items-center justify-center bg-zinc-100 border border-zinc-200 text-sm text-zinc-700 hover:underline">
                Download PDF
              </a>
            )}
            <div className="mt-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs truncate" title={f.original_filename}>{f.original_filename}</p>
                <p className="text-[10px] text-zinc-500 font-mono">{formatDateTime(f.created_at)}</p>
              </div>
              <button
                onClick={() => onRemove(f.file_id)}
                className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 rounded"
                data-testid={`remove-file-${f.file_id}`}
                title="Remove"
              >
                <Trash size={12} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Metric({ label, value, icon: Icon, accent }) {
  return (
    <div className={`border p-4 bg-white ${accent ? "border-zinc-900" : "border-zinc-200"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} weight="bold" className={accent ? "text-zinc-900" : "text-zinc-500"} />
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</p>
      </div>
      <p className="text-2xl font-black tracking-tighter font-mono">{value}</p>
    </div>
  );
}
